import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Menu, theme } from 'antd';

import {
  useGetErrorRateChartQuery,
  useGetPagesCrawledChartQuery,
  type DiscoverabilityChartQueryParams,
} from '../api/discoverabilityChartsApi';
import {
  AiInsightsSectionHeading,
  expandProviderCrawlerIds,
  mapNormalizedToProvider,
  useAiCrawlerLineup,
} from '../../shared';
import { CustomTooltip } from '~components/Tooltip';
import CrawlerActivityChart from './CrawlerActivityChart';
import { ALL_CRAWLERS_LABEL } from './CrawlerActivityChartLegend';
import type {
  DiscoverabilityChartKey,
  DiscoverabilityChartToggleAction,
} from '../DiscoverabilityAnalyticsEvents';
import { OTHER_PROVIDER } from '../parseChartResponseData';
import { useDiscoverabilityEntitlement } from '../hooks/useDiscoverabilityEntitlement';
import { useDiscoverabilitySearchParams } from '../hooks/useDiscoverabilitySearchParams';
import { USER_TIMEZONE } from '../userTimezone';
import useTrackDiscoverabilityEvent from '../hooks/useTrackDiscoverabilityEvent';

const PAGES_CRAWLED: DiscoverabilityChartKey = 'pages-crawled';
const ERROR_RATE: DiscoverabilityChartKey = 'error-rate';

export const CRAWL_REQUESTS_TAB_TOOLTIP =
  'Shows the volume of requests each AI crawler made to your site over the selected period. Use it to see which crawlers are requesting your pages and to spot sustained declines that could signal access issues. Values are smoothed over 7 days to highlight trends rather than daily fluctuations.';

export const ERROR_RATE_TAB_TOOLTIP =
  "Shows the percentage of each AI crawler's requests that returned a server error (5xx). Use it to spot periods where crawlers had trouble accessing your pages. Persistent errors can limit how much of your content AI crawlers can reach. Values are smoothed over 7 days to highlight trends rather than daily fluctuations.";

type CrawlerActivityChartsSectionProps = {
  impressionsReady?: boolean;
  // Account has never been visited by an AI crawler (the KPI response's
  // `hasEverHadCrawlerActivity` is false) — shared from DiscoverabilityView so
  // the chart shows the same "Waiting" state as the KPIs and Pages sections
  // instead of a flat-zero line (PRE-3098).
  waitingForCrawlerRequests?: boolean;
};

function CrawlerActivityChartsSection({
  impressionsReady = true,
  waitingForCrawlerRequests = false,
}: CrawlerActivityChartsSectionProps) {
  const { token } = theme.useToken();
  const trackEvent = useTrackDiscoverabilityEvent();
  const entitlement = useDiscoverabilityEntitlement();
  const isNoIntegration = entitlement.kind === 'no-integration';
  const [activeTab, setActiveTab] = useState<DiscoverabilityChartKey>(PAGES_CRAWLED);
  const previousTabRef = useRef<DiscoverabilityChartKey | undefined>(undefined);

  const { dateFrom, dateTo, crawlers, domain } = useDiscoverabilitySearchParams();
  const lineup = useAiCrawlerLineup();

  const params = useMemo<DiscoverabilityChartQueryParams>(
    () => ({
      dateFrom,
      dateTo,
      crawlers,
      domain,
      timezone: USER_TIMEZONE,
    }),
    [dateFrom, dateTo, crawlers, domain]
  );

  const pagesQuery = useGetPagesCrawledChartQuery(params, {
    skip: activeTab !== PAGES_CRAWLED,
  });
  const errorQuery = useGetErrorRateChartQuery(params, {
    skip: activeTab !== ERROR_RATE,
  });

  const [hiddenSeries, setHiddenSeries] = useState<ReadonlySet<string>>(() => new Set());

  const activeQuery = activeTab === PAGES_CRAWLED ? pagesQuery : errorQuery;

  // The All Crawlers line must aggregate only the crawlers still visible on
  // the chart (PRE-3094). The server owns the aggregation semantics (plain
  // sums for pages-crawled, pooled ratios for error-rate — the latter cannot
  // be derived from the per-crawler series), so run the same chart query
  // scoped to the visible crawler ids and plot its `all`. `undefined` means
  // no provider is hidden: no scoped query is needed and the chart plots the
  // main response's `all` as before.
  const visibleCrawlers = useMemo<string[] | undefined>(() => {
    const hiddenProviders = new Set(hiddenSeries);
    hiddenProviders.delete(ALL_CRAWLERS_LABEL);
    if (hiddenProviders.size === 0) return undefined;
    // When the URL carries a crawler filter, those exact ids are the scope the
    // server applied — reuse them verbatim so the scoped query never widens
    // beyond the filter (the response keys can't be used for this: error-rate
    // folds them into one canonical id per provider, PRE-3145). Without a URL
    // filter, derive the scope from the response's own per-point crawler keys
    // (avoiding a client-side mirror of the backend default list), expanding
    // each key to its provider's full id set so pre-blended canonical
    // error-rate keys don't silently drop sub-label rows from the SQL filter.
    const selected =
      params.crawlers ??
      Array.from(
        new Set(
          Object.keys(activeQuery.data?.data[0]?.crawlers ?? {}).flatMap((id) =>
            expandProviderCrawlerIds(lineup, id)
          )
        )
      );
    return selected.filter(
      (id) => !hiddenProviders.has(mapNormalizedToProvider(lineup, id) ?? OTHER_PROVIDER)
    );
  }, [hiddenSeries, activeQuery.data, params.crawlers, lineup]);

  const scopedParams = useMemo<DiscoverabilityChartQueryParams>(
    () => (visibleCrawlers ? { ...params, crawlers: visibleCrawlers } : params),
    [params, visibleCrawlers]
  );

  // Skipped when nothing is hidden (the main response already carries the
  // right `all`) and when everything is hidden (an empty crawler list must
  // never reach the API — it serializes to no `crawlers` params at all,
  // which the backend reads as "every crawler").
  const skipScopedQuery = visibleCrawlers === undefined || visibleCrawlers.length === 0;

  const pagesAllCrawlersQuery = useGetPagesCrawledChartQuery(scopedParams, {
    skip: activeTab !== PAGES_CRAWLED || skipScopedQuery,
  });
  const errorAllCrawlersQuery = useGetErrorRateChartQuery(scopedParams, {
    skip: activeTab !== ERROR_RATE || skipScopedQuery,
  });

  // Fire once per genuine chart-tab impression. The ref starts undefined, so
  // mount counts as the first impression (the default Crawl Requests tab).
  // Suppressed while the view is behind the no-integration blur, and re-fires
  // on a real tab switch (the ref tracks the last tab that fired). Also waits
  // until the parent signals the view has settled (impressionsReady), so a
  // transient mount before the KPIs resolve does not fire a spurious impression.
  useEffect(() => {
    if (isNoIntegration || !impressionsReady || previousTabRef.current === activeTab) return;
    previousTabRef.current = activeTab;
    trackEvent(
      activeTab === ERROR_RATE
        ? 'Discoverability: Error Rate Tab Viewed'
        : 'Discoverability: Crawl Requests Tab Viewed'
    );
  }, [activeTab, isNoIntegration, impressionsReady, trackEvent]);

  const handleSeriesToggled = useCallback(
    (series: string, action: DiscoverabilityChartToggleAction) => {
      setHiddenSeries((prev) => {
        const next = new Set(prev);
        if (action === 'hide') next.add(series);
        else next.delete(series);
        return next;
      });
      trackEvent('Discoverability: Chart Series Toggled', {
        chart: activeTab,
        series,
        action,
      });
    },
    [activeTab, trackEvent]
  );

  const activeAllCrawlersQuery =
    activeTab === PAGES_CRAWLED ? pagesAllCrawlersQuery : errorAllCrawlersQuery;
  const valueFormat = activeTab === PAGES_CRAWLED ? 'integer' : 'percent';

  // While providers are hidden, only ever plot a server-computed aggregate of
  // exactly the visible crawlers: `currentData` (unlike `data`) is always for
  // the current params, so no stale or unscoped values are plotted while the
  // scoped query loads or after it errors — the line is simply absent. An
  // empty visible set has no aggregate to show, for the same reason.
  const allCrawlersValues = useMemo<number[] | undefined>(() => {
    if (visibleCrawlers === undefined) return undefined;
    if (visibleCrawlers.length === 0) return [];
    return activeAllCrawlersQuery.currentData?.data.map((point) => point.all) ?? [];
  }, [visibleCrawlers, activeAllCrawlersQuery.currentData]);

  return (
    <Flex vertical gap={token.marginXS}>
      <AiInsightsSectionHeading
        title="AI Crawler Activity"
        description="Track the rolling average of AI Crawler activity and error rates over time."
      />

      <Flex vertical>
        <Menu
          mode="horizontal"
          selectedKeys={[activeTab]}
          onClick={({ key }) => setActiveTab(key as DiscoverabilityChartKey)}
          items={[
            { key: PAGES_CRAWLED, label: 'Crawl Requests', tooltip: CRAWL_REQUESTS_TAB_TOOLTIP },
            { key: ERROR_RATE, label: 'Error Rate', tooltip: ERROR_RATE_TAB_TOOLTIP },
          ].map(({ key, label, tooltip }) => ({
            key,
            label: (
              <CustomTooltip placement="top" message={tooltip}>
                {label}
              </CustomTooltip>
            ),
          }))}
        />
        <CrawlerActivityChart
          valueFormat={valueFormat}
          data={activeQuery.data}
          allCrawlersValues={allCrawlersValues}
          isLoading={activeQuery.isLoading}
          waitingForCrawlerRequests={waitingForCrawlerRequests}
          hiddenSeries={hiddenSeries}
          onSeriesToggled={handleSeriesToggled}
        />
      </Flex>
    </Flex>
  );
}

export default CrawlerActivityChartsSection;
